import type { BreakdownItem } from '@/types/analytics'
import { getBreakdownItemColor } from '@/utils/chartUtils/breakdownItemColor'
import { cn } from '@/utils/cn'
import { formatDecimalToPercentage, formatUSD } from '@vaultsfyi/common/utils'
import type { ReactNode } from 'react'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { ChartContainer } from './chartContainer'
import { ChartTooltip } from './chartTooltip'

function DefaultTooltip({ data }: { data: BreakdownItem | undefined }) {
  if (!data) return null

  return (
    <ChartTooltip>
      <p className="flex justify-between gap-2 whitespace-nowrap">
        <span>{data.displayName}</span>{' '}
        <span>
          {formatUSD(data.value)} ({formatDecimalToPercentage(data.percentage)})
        </span>
      </p>
    </ChartTooltip>
  )
}

export function BreakdownChart({
  items,
  size = 152,
  className,
  renderTooltip,
}: {
  items: BreakdownItem[]
  size?: number
  className?: string
  renderTooltip?: (data: BreakdownItem | undefined) => ReactNode
}) {
  const outerRadius = size / 2 - 4
  const innerRadius = Math.round(outerRadius * 0.55)

  return (
    <ChartContainer className={cn('size-36 min-w-36', className)}>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={items}
            cx="50%"
            cy="50%"
            startAngle={90}
            endAngle={-270}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            dataKey="value"
            isAnimationActive={false}
            stroke="none"
            tabIndex={-1}
          >
            {items.map((item, index) => (
              <Cell key={item.name} fill={getBreakdownItemColor(item, index)} className="focus:outline-none" />
            ))}
          </Pie>
          <Tooltip
            wrapperStyle={{ zIndex: 50 }}
            content={({ payload }) => {
              const data = payload?.[0]?.payload as BreakdownItem | undefined
              return renderTooltip ? renderTooltip(data) : <DefaultTooltip data={data} />
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}
